//Created By Cursor
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  InputAdornment,
} from "@mui/material";
import {
  ReceiptLong as TransactionIcon,
  Search as SearchIcon,
  Today as TodayIcon,
  DateRange as DateRangeIcon,
  CalendarMonth as MonthIcon,
  History as AsOnDateIcon,
  Refresh as RefreshIcon,
  School as SchoolIcon,
  CheckCircle as CheckIcon,
  InfoOutlined as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";
import PageContentLayout from "../components/layout/PageContentLayout";
import apiService from "../services/apiService";
import { crmColors } from "../theme/colors";

const REPORT_TYPES = [
  { id: "today", label: "Today Collection", icon: TodayIcon, color: "#10b981" },
  { id: "asOnDate", label: "As On Date", icon: AsOnDateIcon, color: "#06b6d4" },
  { id: "dateRange", label: "Date Range", icon: DateRangeIcon, color: "#3b82f6" },
  { id: "month", label: "Month Wise", icon: MonthIcon, color: "#8b5cf6" },
];

const DATE_RANGE_PRESETS = [
  { value: "today", label: "Today", color: "#10b981" },
  { value: "yesterday", label: "Yesterday", color: "#6366f1" },
  { value: "thisWeek", label: "This Week", color: "#3b82f6" },
  { value: "lastWeek", label: "Last Week", color: "#a855f7" },
  { value: "last7Days", label: "Last 7 Days", color: "#8b5cf6" },
  { value: "thisMonth", label: "This Month", color: "#f59e0b" },
  { value: "lastMonth", label: "Last Month", color: "#ef4444" },
  { value: "last30Days", label: "Last 30 Days", color: "#06b6d4" },
];

const formatDateLocal = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const parseLooseDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatMoney = (value) => {
  const n = Number(value);
  if (value == null || Number.isNaN(n)) return "—";
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const txnYearMonth = (paymentDate) => {
  if (!paymentDate) return "";
  const d = new Date(paymentDate);
  if (Number.isNaN(d.getTime())) {
    const s = String(paymentDate);
    return s.length >= 7 ? s.slice(0, 7) : "";
  }
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const monthsBetween = (startRaw, endRaw) => {
  const start = parseLooseDate(startRaw);
  const end = parseLooseDate(endRaw);
  if (!start || !end) return [];
  const opts = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cursor <= endMonth) {
    opts.push(
      `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`
    );
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return opts;
};

const fyIdOf = (fy) =>
  fy?.financial_year_id || fy?.year_id || fy?.id || fy?.year || "";

const fyLabelOf = (fy) =>
  fy?.financial_year_name ||
  fy?.year_name ||
  fy?.name ||
  String(fyIdOf(fy) || "—");

const rowSearchText = (r) =>
  [
    r.school_name,
    r.school_code,
    r.transaction_id,
    r.gateway_transaction_id,
    r.order_id,
    r.invoice_id,
    r.payment_method,
    r.payment_channel,
    r.payment_status,
    r.product_name,
  ]
    .map((v) => String(v || "").toLowerCase())
    .join(" ");

const statusTone = (status) => {
  const t = String(status || "").toLowerCase();
  if (t === "received") return { bg: alpha(crmColors.success.main, 0.12), fg: crmColors.success.dark };
  if (t === "pending") return { bg: alpha(crmColors.warning.main, 0.14), fg: crmColors.warning.dark };
  return { bg: alpha(crmColors.secondary.main, 0.1), fg: crmColors.secondary.dark };
};

const panelCardSx = {
  borderRadius: "16px",
  overflow: "hidden",
  border: "2px solid #e5e7eb",
  boxShadow: "0 4px 12px rgba(0,0,0,0.06), 0 2px 4px rgba(0,0,0,0.03)",
  bgcolor: crmColors.background.paper,
  background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#fff",
  },
};

// Created By Cursor — CRM Transaction Panel from tm_crm_order_payment
const TransactionPanel = () => {
  const [financialYears, setFinancialYears] = useState([]);
  const [financialYearId, setFinancialYearId] = useState("");
  const [fyLoading, setFyLoading] = useState(false);

  const [schoolCodeInput, setSchoolCodeInput] = useState("");
  const [schoolCode, setSchoolCode] = useState("");

  const [reportType, setReportType] = useState("today");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateRangePreset, setDateRangePreset] = useState("");
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [selectingStartDate, setSelectingStartDate] = useState(true);
  const [yearMonth, setYearMonth] = useState("");
  const [monthOptions, setMonthOptions] = useState([]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const [filterStatus, setFilterStatus] = useState("");
  const [filterMethod, setFilterMethod] = useState("");
  const [filterChannel, setFilterChannel] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [sortBy, setSortBy] = useState("payment_date");
  const [sortOrder, setSortOrder] = useState("DESC");

  const selectedFy = useMemo(
    () => financialYears.find((fy) => String(fyIdOf(fy)) === String(financialYearId)),
    [financialYears, financialYearId]
  );

  const activeReport = REPORT_TYPES.find((t) => t.id === reportType) || REPORT_TYPES[0];

  // Created By Cursor — financial year start → today
  const resolveAsOnDateBounds = useCallback(() => {
    const today = formatDateLocal(new Date());
    const fyStart = parseLooseDate(selectedFy?.start_date);
    return {
      from: fyStart ? formatDateLocal(fyStart) : today,
      to: today,
    };
  }, [selectedFy]);

  // Created By Cursor — quick date-range presets (EduOKWeb style)
  const handleDateRangePreset = useCallback((preset) => {
    const today = new Date();
    let newStart = "";
    let newEnd = "";

    switch (preset) {
      case "today":
        newStart = newEnd = formatDateLocal(today);
        break;
      case "yesterday": {
        const d = new Date(today);
        d.setDate(today.getDate() - 1);
        newStart = newEnd = formatDateLocal(d);
        break;
      }
      case "thisWeek": {
        const start = new Date(today);
        start.setDate(today.getDate() - today.getDay());
        newStart = formatDateLocal(start);
        newEnd = formatDateLocal(today);
        break;
      }
      case "lastWeek": {
        const end = new Date(today);
        end.setDate(today.getDate() - today.getDay() - 1);
        const start = new Date(end);
        start.setDate(end.getDate() - 6);
        newStart = formatDateLocal(start);
        newEnd = formatDateLocal(end);
        break;
      }
      case "last7Days": {
        const start = new Date(today);
        start.setDate(today.getDate() - 7);
        newStart = formatDateLocal(start);
        newEnd = formatDateLocal(today);
        break;
      }
      case "thisMonth": {
        newStart = formatDateLocal(new Date(today.getFullYear(), today.getMonth(), 1));
        newEnd = formatDateLocal(today);
        break;
      }
      case "lastMonth": {
        newStart = formatDateLocal(new Date(today.getFullYear(), today.getMonth() - 1, 1));
        newEnd = formatDateLocal(new Date(today.getFullYear(), today.getMonth(), 0));
        break;
      }
      case "last30Days": {
        const start = new Date(today);
        start.setDate(today.getDate() - 30);
        newStart = formatDateLocal(start);
        newEnd = formatDateLocal(today);
        break;
      }
      default:
        return;
    }

    setDateRangePreset(preset);
    setStartDate(newStart);
    setEndDate(newEnd);
    setSelectingStartDate(true);
    if (newStart) {
      const d = parseLooseDate(newStart);
      if (d) setCalendarMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    }
  }, []);

  // Created By Cursor — EduOKWeb calendar day click (start then end)
  const handleCalendarDateClick = useCallback(
    (date) => {
      const dateStr = formatDateLocal(date);
      if (selectingStartDate || !startDate || dateStr < startDate) {
        setStartDate(dateStr);
        setEndDate("");
        setSelectingStartDate(false);
        setDateRangePreset("custom");
      } else {
        setEndDate(dateStr);
        setSelectingStartDate(true);
        setDateRangePreset("custom");
      }
    },
    [selectingStartDate, startDate]
  );

  const navigateCalendarMonth = useCallback((direction) => {
    setCalendarMonth((prev) => {
      const next = new Date(prev);
      next.setMonth(prev.getMonth() + direction);
      return next;
    });
  }, []);

  const calendarDays = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    const days = [];
    for (let day = 1; day <= lastDay; day++) {
      days.push(new Date(year, month, day));
    }
    return days;
  }, [calendarMonth]);

  const isDateInRange = useCallback(
    (date) => {
      if (!startDate || !endDate) return false;
      const dateStr = formatDateLocal(date);
      return dateStr >= startDate && dateStr <= endDate;
    },
    [startDate, endDate]
  );

  const isDateSelected = useCallback(
    (date) => {
      const dateStr = formatDateLocal(date);
      return dateStr === startDate || dateStr === endDate;
    },
    [startDate, endDate]
  );

  const isTodayDate = useCallback((date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }, []);

  const filteredRows = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    let list = (rows || []).filter((r) => {
      if (
        filterStatus &&
        String(r.payment_status || "").toLowerCase() !== filterStatus.toLowerCase()
      ) {
        return false;
      }
      if (
        filterMethod &&
        String(r.payment_method || "").toLowerCase() !== filterMethod.toLowerCase()
      ) {
        return false;
      }
      if (
        filterChannel &&
        String(r.payment_channel || "Manual").toLowerCase() !== filterChannel.toLowerCase()
      ) {
        return false;
      }
      if (filterMonth && txnYearMonth(r.payment_date) !== filterMonth) return false;
      if (q && !rowSearchText(r).includes(q)) return false;
      return true;
    });

    const mult = sortOrder === "DESC" ? -1 : 1;
    list = [...list].sort((a, b) => {
      if (sortBy === "school_name") {
        const va = (a.school_name || a.school_code || "").toLowerCase();
        const vb = (b.school_name || b.school_code || "").toLowerCase();
        if (va < vb) return -mult;
        if (va > vb) return mult;
        return 0;
      }
      if (sortBy === "amount") {
        return (
          mult *
          ((Number(a.transaction_amount) || 0) - (Number(b.transaction_amount) || 0))
        );
      }
      const ta = new Date(a.payment_date || 0).getTime();
      const tb = new Date(b.payment_date || 0).getTime();
      return mult * (ta - tb);
    });
    return list;
  }, [
    rows,
    filterStatus,
    filterMethod,
    filterChannel,
    filterMonth,
    searchQ,
    sortBy,
    sortOrder,
  ]);

  const totalPaid = useMemo(
    () =>
      filteredRows.reduce((sum, r) => {
        const v = Number(r.transaction_amount);
        return sum + (Number.isFinite(v) ? v : 0);
      }, 0),
    [filteredRows]
  );

  // Created By Cursor — load CRM financial years on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setFyLoading(true);
      const result = await apiService.getFinancialYearList();
      if (cancelled) return;
      setFyLoading(false);
      if (result.success) {
        const list = Array.isArray(result.data) ? result.data : [];
        setFinancialYears(list);
        const current =
          list.find((fy) => {
            const status = String(fy.status || fy.financial_year_status || "").toLowerCase();
            return status === "current";
          }) || list[0];
        if (current) setFinancialYearId(String(fyIdOf(current)));
      } else {
        toast.error(result.error || "Failed to load financial years");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (selectedFy?.start_date && selectedFy?.end_date) {
      setMonthOptions(monthsBetween(selectedFy.start_date, selectedFy.end_date));
    } else {
      setMonthOptions([]);
    }
    setYearMonth("");
  }, [selectedFy]);

  // Created By Cursor
  const applySchool = () => {
    const code = schoolCodeInput.trim();
    setSchoolCode(code);
    setRows([]);
    setLoaded(false);
    if (code) toast.success(`Filter: ${code}`);
    else toast.success("Showing all schools");
  };

  // Created By Cursor
  const loadBatch = useCallback(async () => {
    const params = { school_code: schoolCode || "" };

    if (reportType === "today") {
      params.report_type = "today";
      params.date = formatDateLocal(new Date());
    } else if (reportType === "asOnDate") {
      const bounds = resolveAsOnDateBounds();
      if (!bounds.from || !bounds.to) {
        toast.error("Financial year start date is missing");
        return;
      }
      params.report_type = "as_on_date";
      params.start_date = bounds.from;
      params.end_date = bounds.to;
    } else if (reportType === "dateRange") {
      if (!startDate || !endDate) {
        toast.error("Select start and end dates");
        return;
      }
      params.report_type = "date_range";
      params.start_date = startDate;
      params.end_date = endDate;
    } else if (reportType === "month") {
      if (!yearMonth) {
        toast.error("Select a month");
        return;
      }
      params.report_type = "month";
      params.year_month = yearMonth;
    }

    setLoading(true);
    setLoaded(true);
    const result = await apiService.getCrmOrderPayments(params);
    setLoading(false);

    if (result.success) {
      setRows(result.data || []);
      if (!(result.data || []).length) toast("No payments found");
    } else {
      setRows([]);
      toast.error(result.error || "Failed to load payments");
    }
  }, [
    schoolCode,
    reportType,
    startDate,
    endDate,
    yearMonth,
    resolveAsOnDateBounds,
  ]);

  useEffect(() => {
    if (reportType === "today") {
      loadBatch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, schoolCode]);

  useEffect(() => {
    if (reportType === "asOnDate" && financialYearId) {
      const bounds = resolveAsOnDateBounds();
      setStartDate(bounds.from);
      setEndDate(bounds.to);
      loadBatch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, schoolCode, financialYearId]);

  useEffect(() => {
    if (reportType === "dateRange" && startDate && endDate) {
      loadBatch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, schoolCode, startDate, endDate]);

  useEffect(() => {
    if (reportType === "month" && yearMonth) {
      loadBatch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, schoolCode, yearMonth]);

  const switchReport = (id) => {
    setReportType(id);
    setLoaded(false);
    setRows([]);
    setDateRangePreset("");
    if (id === "asOnDate") {
      const bounds = resolveAsOnDateBounds();
      setStartDate(bounds.from);
      setEndDate(bounds.to);
    }
  };

  const asOnBounds = resolveAsOnDateBounds();
  const asOnFromLabel =
    parseLooseDate(asOnBounds.from)?.toLocaleDateString("en-IN") || asOnBounds.from;

  return (
    <PageContentLayout
      title="Transaction Panel"
      subtitle="CRM order payments from tm_crm_order_payment"
      barSubtitle={
        [
          selectedFy ? fyLabelOf(selectedFy) : null,
          schoolCode || "All schools",
          `${filteredRows.length} payments`,
        ]
          .filter(Boolean)
          .join(" · ")
      }
      breadcrumbs={[
        {
          label: "Transaction Panel",
          icon: <TransactionIcon sx={{ color: "#ffffff", fontSize: 18 }} />,
        },
      ]}
      backTo="/dashboard"
      darkBar
      titleIcon={<TransactionIcon />}
      actions={
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={loadBatch}
          disabled={loading || (reportType === "month" && !yearMonth)}
          sx={{
            textTransform: "none",
            fontWeight: 800,
            borderRadius: "8px",
            color: "#fff",
            border: `1px solid ${alpha("#fff", 0.35)}`,
            bgcolor: alpha("#fff", 0.12),
            "&:hover": { bgcolor: alpha("#fff", 0.22) },
          }}
        >
          Refresh
        </Button>
      }
    >
      <Stack spacing={2.25}>
        <Box sx={panelCardSx}>
          <Box
            sx={{
              px: 2.25,
              py: 1.5,
              background: `linear-gradient(135deg, ${crmColors.primary.main} 0%, ${crmColors.primary.dark} 100%)`,
              color: "#fff",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.25}
              alignItems={{ md: "center" }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "12px",
                  display: "grid",
                  placeItems: "center",
                  bgcolor: alpha("#fff", 0.18),
                  flexShrink: 0,
                }}
              >
                <SchoolIcon sx={{ fontSize: 22 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: 15, lineHeight: 1.25 }}>
                  Order payments
                </Typography>
                <Typography sx={{ fontSize: 12, opacity: 0.88, fontWeight: 600, mt: 0.15 }}>
                  {schoolCode
                    ? `Filtered by school ${schoolCode}`
                    : "All schools · optional school filter"}
                </Typography>
              </Box>

              <FormControl
                size="small"
                sx={{
                  minWidth: 260,
                  width: { xs: "100%", md: 280 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    bgcolor: alpha("#fff", 0.95),
                    fontWeight: 700,
                  },
                }}
                disabled={fyLoading || !financialYears.length}
              >
                <InputLabel>Financial year</InputLabel>
                <Select
                  label="Financial year"
                  value={financialYearId}
                  onChange={(e) => setFinancialYearId(String(e.target.value))}
                  IconComponent={ExpandMoreIcon}
                >
                  {financialYears.map((fy) => (
                    <MenuItem key={fyIdOf(fy)} value={String(fyIdOf(fy))}>
                      {fyLabelOf(fy)}
                      {fy.status ? ` (${fy.status})` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                placeholder="School code (optional)"
                value={schoolCodeInput}
                onChange={(e) => setSchoolCodeInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applySchool()}
                sx={{
                  minWidth: 170,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    bgcolor: alpha("#fff", 0.95),
                    fontWeight: 700,
                  },
                }}
              />
              <Button
                variant="contained"
                disableElevation
                onClick={applySchool}
                sx={{
                  textTransform: "none",
                  fontWeight: 800,
                  borderRadius: "10px",
                  bgcolor: crmColors.energy.main,
                  color: "#fff",
                  px: 2,
                  "&:hover": { bgcolor: crmColors.energy.dark },
                }}
              >
                Apply
              </Button>
            </Stack>
          </Box>

          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                  md: "repeat(4, 1fr)",
                },
                gap: 1.25,
              }}
            >
              {REPORT_TYPES.map((option) => {
                const isSelected = reportType === option.id;
                const Icon = option.icon;
                return (
                  <Box
                    key={option.id}
                    onClick={() => switchReport(option.id)}
                    sx={{
                      p: 1.35,
                      borderRadius: "12px",
                      border: `2px solid ${isSelected ? option.color : "#e5e7eb"}`,
                      background: isSelected
                        ? `linear-gradient(135deg, ${option.color}18 0%, ${option.color}08 100%)`
                        : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                      cursor: "pointer",
                      transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                      transform: isSelected ? "translateY(-2px)" : "none",
                      boxShadow: isSelected
                        ? `0 6px 16px ${option.color}33`
                        : "0 2px 4px rgba(0,0,0,0.04)",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        borderColor: isSelected ? option.color : "#cbd5e1",
                        boxShadow: `0 6px 14px ${alpha(option.color, 0.2)}`,
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1.1} alignItems="center">
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: "9px",
                          display: "grid",
                          placeItems: "center",
                          background: isSelected
                            ? `linear-gradient(135deg, ${option.color} 0%, ${option.color}dd 100%)`
                            : `linear-gradient(135deg, ${option.color}22 0%, ${option.color}12 100%)`,
                          color: isSelected ? "#fff" : option.color,
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ fontSize: 18 }} />
                      </Box>
                      <Typography
                        sx={{
                          flex: 1,
                          fontSize: 13.5,
                          fontWeight: isSelected ? 800 : 700,
                          color: isSelected ? option.color : "#374151",
                          lineHeight: 1.25,
                        }}
                      >
                        {option.label}
                      </Typography>
                      {isSelected ? (
                        <CheckIcon sx={{ fontSize: 18, color: option.color }} />
                      ) : null}
                    </Stack>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        <Box sx={{ ...panelCardSx, p: 2.25, borderWidth: 2 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 15.5,
              mb: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "#111827",
            }}
          >
            <Box
              component="span"
              sx={{
                width: 28,
                height: 28,
                borderRadius: "8px",
                display: "inline-grid",
                placeItems: "center",
                bgcolor: alpha(activeReport.color, 0.14),
                color: activeReport.color,
              }}
            >
              {React.createElement(activeReport.icon, { sx: { fontSize: 16 } })}
            </Box>
            {activeReport.label}
          </Typography>

          {reportType === "today" && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: "10px",
                bgcolor: "#ecfdf5",
                border: "1px solid #86efac",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <InfoIcon sx={{ color: "#10b981", fontSize: 18 }} />
              <Typography sx={{ color: "#047857", fontWeight: 700, fontSize: 13.5 }}>
                CRM payments received/recorded today:{" "}
                {new Date().toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </Typography>
            </Box>
          )}

          {reportType === "asOnDate" && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: "10px",
                bgcolor: "#ecfeff",
                border: "1px solid #67e8f9",
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <InfoIcon sx={{ color: "#06b6d4", fontSize: 18, mt: 0.15 }} />
              <Typography sx={{ color: "#0e7490", fontWeight: 700, fontSize: 13.5 }}>
                All CRM payments from financial year start ({asOnFromLabel}) to today (
                {new Date().toLocaleDateString("en-IN")}).
                {selectedFy ? ` · FY: ${fyLabelOf(selectedFy)}` : ""}
              </Typography>
            </Box>
          )}

          {reportType === "dateRange" && (
            <Stack spacing={1.5}>
              {/* Quick presets — EduOKWeb style */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr 1fr",
                    sm: "repeat(4, 1fr)",
                    md: "repeat(8, minmax(0, 1fr))",
                  },
                  gap: 1,
                }}
              >
                {DATE_RANGE_PRESETS.map((preset) => {
                  const active = dateRangePreset === preset.value;
                  return (
                    <Box
                      key={preset.value}
                      onClick={() => handleDateRangePreset(preset.value)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        px: 1,
                        py: 1,
                        borderRadius: "10px",
                        cursor: "pointer",
                        border: `2px solid ${active ? preset.color : "#e5e7eb"}`,
                        background: active
                          ? `linear-gradient(135deg, ${preset.color} 0%, ${preset.color}dd 100%)`
                          : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                        color: active ? "#fff" : "#374151",
                        boxShadow: active
                          ? `0 4px 12px ${preset.color}40`
                          : "0 2px 4px rgba(0,0,0,0.05)",
                        transform: active ? "translateY(-2px)" : "none",
                        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          borderColor: preset.color,
                          boxShadow: `0 4px 12px ${alpha(preset.color, 0.3)}`,
                          bgcolor: active ? undefined : alpha(preset.color, 0.08),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: "6px",
                          flexShrink: 0,
                          display: "grid",
                          placeItems: "center",
                          bgcolor: active ? alpha("#fff", 0.22) : alpha(preset.color, 0.12),
                          color: active ? "#fff" : preset.color,
                        }}
                      >
                        <DateRangeIcon sx={{ fontSize: 13 }} />
                      </Box>
                      <Typography
                        sx={{
                          flex: 1,
                          fontSize: 12,
                          fontWeight: active ? 800 : 700,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {preset.label}
                      </Typography>
                      {active ? <CheckIcon sx={{ fontSize: 14, flexShrink: 0 }} /> : null}
                    </Box>
                  );
                })}
              </Box>

              {/* Month calendar strip — EduOKWeb */}
              <Box
                sx={{
                  bgcolor: "#fff",
                  borderRadius: "12px",
                  border: "2px solid #e5e7eb",
                  p: "8px 10px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                    background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                    px: 1.5,
                    py: 1,
                    borderRadius: "10px",
                    border: "1px solid #bfdbfe",
                  }}
                >
                  <Box
                    component="button"
                    type="button"
                    onClick={() => navigateCalendarMonth(-1)}
                    sx={{
                      border: "1px solid #93c5fd",
                      bgcolor: "#fff",
                      borderRadius: "6px",
                      px: 1,
                      py: 0.5,
                      cursor: "pointer",
                      display: "grid",
                      placeItems: "center",
                      color: "#374151",
                      "&:hover": { bgcolor: "#f3f4f6", borderColor: "#60a5fa" },
                    }}
                  >
                    <ChevronLeftIcon sx={{ fontSize: 16 }} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 14, color: "#1e40af", m: 0 }}>
                    {calendarMonth.toLocaleString("default", {
                      month: "short",
                      year: "numeric",
                    })}
                  </Typography>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => navigateCalendarMonth(1)}
                    sx={{
                      border: "1px solid #93c5fd",
                      bgcolor: "#fff",
                      borderRadius: "6px",
                      px: 1,
                      py: 0.5,
                      cursor: "pointer",
                      display: "grid",
                      placeItems: "center",
                      color: "#374151",
                      "&:hover": { bgcolor: "#f3f4f6", borderColor: "#60a5fa" },
                    }}
                  >
                    <ChevronRightIcon sx={{ fontSize: 16 }} />
                  </Box>
                </Box>

                <Box sx={{ display: "flex", gap: "1px", mb: "3px", width: "100%" }}>
                  {calendarDays.map((date, index) => {
                    const dayNames = ["S", "M", "T", "W", "T", "F", "S"];
                    return (
                      <Box
                        key={`hdr-${index}`}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          height: 24,
                          display: "grid",
                          placeItems: "center",
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#475569",
                        }}
                      >
                        {dayNames[date.getDay()]}
                      </Box>
                    );
                  })}
                </Box>

                <Box sx={{ display: "flex", gap: "1px", width: "100%" }}>
                  {calendarDays.map((date) => {
                    const dateStr = formatDateLocal(date);
                    const inRange = isDateInRange(date);
                    const selected = isDateSelected(date);
                    const today = isTodayDate(date);
                    const isStart = dateStr === startDate;
                    const isEnd = dateStr === endDate;
                    return (
                      <Box
                        key={dateStr}
                        component="button"
                        type="button"
                        onClick={() => handleCalendarDateClick(date)}
                        title={date.toLocaleDateString("en-IN")}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          aspectRatio: "1",
                          minHeight: 32,
                          p: "4px 1px",
                          borderRadius: "50%",
                          cursor: "pointer",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 12,
                          fontWeight: selected ? 800 : 600,
                          border: selected
                            ? "2px solid #3b82f6"
                            : inRange
                              ? "1px solid #bfdbfe"
                              : "1px solid transparent",
                          background:
                            isStart || isEnd
                              ? "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
                              : inRange
                                ? "#eff6ff"
                                : today
                                  ? "#fef3c7"
                                  : "#e2e8f0",
                          color:
                            isStart || isEnd
                              ? "#fff"
                              : today && !inRange
                                ? "#92400e"
                                : "#374151",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            bgcolor:
                              selected || inRange || today ? undefined : "#f1f5f9",
                          },
                        }}
                      >
                        <Box component="span" sx={{ fontSize: 13, lineHeight: 1.3, fontWeight: 700 }}>
                          {date.getDate()}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* Custom date inputs */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ sm: "center" }}
                sx={{ mt: 0.5 }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1, minWidth: 200 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#374151", whiteSpace: "nowrap" }}>
                    Start Date
                  </Typography>
                  <TextField
                    size="small"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setEndDate("");
                      setSelectingStartDate(false);
                      setDateRangePreset("custom");
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        bgcolor: "linear-gradient(135deg, #fff 0%, #f8fafc 100%)",
                        fontWeight: 700,
                        border: "2px solid #e5e7eb",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                        "& fieldset": { border: "none" },
                        "&:hover": { boxShadow: "0 4px 8px rgba(59,130,246,0.1)" },
                        "&.Mui-focused": {
                          boxShadow: "0 0 0 3px rgba(59,130,246,0.12), 0 4px 12px rgba(59,130,246,0.15)",
                        },
                      },
                    }}
                  />
                </Stack>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1, minWidth: 200 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#374151", whiteSpace: "nowrap" }}>
                    End Date
                  </Typography>
                  <TextField
                    size="small"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setSelectingStartDate(true);
                      setDateRangePreset("custom");
                    }}
                    inputProps={{ min: startDate || undefined }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        fontWeight: 700,
                        border: "2px solid #e5e7eb",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                        "& fieldset": { border: "none" },
                        "&:hover": { boxShadow: "0 4px 8px rgba(59,130,246,0.1)" },
                        "&.Mui-focused": {
                          boxShadow: "0 0 0 3px rgba(59,130,246,0.12), 0 4px 12px rgba(59,130,246,0.15)",
                        },
                      },
                    }}
                  />
                </Stack>
              </Stack>

              {startDate && endDate ? (
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: "8px",
                    bgcolor: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <InfoIcon sx={{ color: "#3b82f6", fontSize: 16 }} />
                  <Typography sx={{ color: "#1e40af", fontWeight: 600, fontSize: 13 }}>
                    Selected:{" "}
                    {parseLooseDate(startDate)?.toLocaleDateString("en-IN") || startDate}
                    {" - "}
                    {parseLooseDate(endDate)?.toLocaleDateString("en-IN") || endDate}
                  </Typography>
                </Box>
              ) : (
                <Typography sx={{ fontWeight: 600, fontSize: 12.5, color: "#64748b" }}>
                  {selectingStartDate || !startDate
                    ? "Click a calendar day to set start date"
                    : "Click another day to set end date"}
                </Typography>
              )}
            </Stack>
          )}

          {reportType === "month" && (
            <Box>
              {monthOptions.length === 0 ? (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: "8px",
                    bgcolor: "#fef3c7",
                    border: "1px solid #fbbf24",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <InfoIcon sx={{ color: "#f59e0b", fontSize: 18 }} />
                  <Typography sx={{ color: "#92400e", fontWeight: 600, fontSize: 13 }}>
                    No months available. Select a financial year with valid dates.
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "nowrap",
                    gap: 1.25,
                    overflowX: "auto",
                    p: 0.5,
                    m: -0.5,
                    "&::-webkit-scrollbar": { height: 6 },
                    "&::-webkit-scrollbar-thumb": {
                      bgcolor: alpha("#8b5cf6", 0.35),
                      borderRadius: 8,
                    },
                  }}
                >
                  {monthOptions.map((m) => {
                    const active = yearMonth === m;
                    const date = parseLooseDate(`${m}-01`);
                    const monthName = date
                      ? date.toLocaleString("default", { month: "long" })
                      : m;
                    return (
                      <Box
                        key={m}
                        onClick={() => setYearMonth(m)}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 1,
                          px: 1.5,
                          py: 1,
                          minWidth: 88,
                          flexShrink: 0,
                          borderRadius: "10px",
                          cursor: "pointer",
                          border: `2px solid ${active ? "#8b5cf6" : "#e5e7eb"}`,
                          background: active
                            ? "linear-gradient(135deg, #8b5cf620 0%, #8b5cf610 100%)"
                            : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                          boxShadow: active
                            ? "0 4px 12px rgba(139,92,246,0.25), 0 2px 4px rgba(0,0,0,0.1)"
                            : "0 2px 4px rgba(0,0,0,0.05)",
                          transform: active ? "translateY(-2px)" : "none",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          fontSize: 13,
                          fontWeight: active ? 800 : 700,
                          color: active ? "#8b5cf6" : "#64748b",
                          boxSizing: "border-box",
                          "&:hover": {
                            transform: active ? "translateY(-3px)" : "translateY(-1px)",
                            borderColor: active ? "#8b5cf6" : "#cbd5e1",
                            boxShadow: active
                              ? "0 6px 16px rgba(139,92,246,0.3)"
                              : "0 4px 8px rgba(0,0,0,0.1)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            border: `2px solid ${active ? "#8b5cf6" : "#cbd5e1"}`,
                            bgcolor: active ? "#8b5cf6" : "transparent",
                            boxShadow: active ? "inset 0 0 0 3px #fff" : "none",
                          }}
                        />
                        <Box sx={{ textAlign: "center", lineHeight: 1.3 }}>
                          <Typography
                            component="span"
                            sx={{ display: "block", fontWeight: "inherit", fontSize: 13 }}
                          >
                            {monthName}
                          </Typography>
                          <Typography
                            component="span"
                            sx={{ display: "block", fontSize: 11, opacity: 0.8 }}
                          >
                            ({m})
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}
        </Box>

        {loaded && (
          <Box sx={{ ...panelCardSx, overflow: "hidden" }}>
            <Box
              sx={{
                px: 2.25,
                py: 1.4,
                background: `linear-gradient(135deg, ${crmColors.primary.main} 0%, ${crmColors.hero.end} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1.5,
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <TransactionIcon sx={{ color: "#fff", fontSize: 20 }} />
                <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>
                  Payment List
                </Typography>
                <Chip
                  size="small"
                  label={`${filteredRows.length} rows`}
                  sx={{
                    height: 24,
                    fontWeight: 800,
                    bgcolor: alpha("#fff", 0.2),
                    color: "#fff",
                    border: `1px solid ${alpha("#fff", 0.3)}`,
                  }}
                />
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography
                  sx={{
                    color: alpha("#fff", 0.9),
                    fontWeight: 800,
                    fontSize: 13,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                >
                  Total
                </Typography>
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 900,
                    fontSize: 18,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatMoney(totalPaid)}
                </Typography>
              </Stack>
            </Box>

            <Box sx={{ p: 2 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                sx={{ mb: 1.75 }}
                flexWrap="wrap"
                useFlexGap
              >
                <TextField
                  size="small"
                  placeholder="Search school, txn, invoice…"
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: crmColors.text.secondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 220, flex: 1, ...fieldSx }}
                />
                <FormControl size="small" sx={{ minWidth: 120, ...fieldSx }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Received">Received</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120, ...fieldSx }}>
                  <InputLabel>Method</InputLabel>
                  <Select
                    label="Method"
                    value={filterMethod}
                    onChange={(e) => setFilterMethod(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Online">Online</MenuItem>
                    <MenuItem value="Transfer">Transfer</MenuItem>
                    <MenuItem value="Offline">Offline</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120, ...fieldSx }}>
                  <InputLabel>Channel</InputLabel>
                  <Select
                    label="Channel"
                    value={filterChannel}
                    onChange={(e) => setFilterChannel(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Manual">Manual</MenuItem>
                    <MenuItem value="Razorpay">Razorpay</MenuItem>
                    <MenuItem value="BillDesk">BillDesk</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120, ...fieldSx }}>
                  <InputLabel>Month</InputLabel>
                  <Select
                    label="Month"
                    value={filterMonth}
                    onChange={(e) => setFilterMonth(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {monthOptions.map((m) => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 130, ...fieldSx }}>
                  <InputLabel>Sort by</InputLabel>
                  <Select
                    label="Sort by"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <MenuItem value="payment_date">Date</MenuItem>
                    <MenuItem value="school_name">School</MenuItem>
                    <MenuItem value="amount">Amount</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 100, ...fieldSx }}>
                  <InputLabel>Order</InputLabel>
                  <Select
                    label="Order"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                  >
                    <MenuItem value="ASC">ASC</MenuItem>
                    <MenuItem value="DESC">DESC</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress size={34} />
                </Box>
              ) : (
                <TableContainer
                  sx={{
                    maxHeight: 520,
                    borderRadius: "12px",
                    border: `1px solid ${crmColors.border.default}`,
                  }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {[
                          "Txn",
                          "School",
                          "Invoice",
                          "Date",
                          "Channel",
                          "Method",
                          "Status",
                          "Amount",
                        ].map((h) => (
                          <TableCell
                            key={h}
                            align={h === "Amount" ? "right" : "left"}
                            sx={{
                              fontWeight: 800,
                              fontSize: 12,
                              letterSpacing: "0.03em",
                              textTransform: "uppercase",
                              color: crmColors.text.secondary,
                              bgcolor: alpha(crmColors.primary.main, 0.05),
                              whiteSpace: "nowrap",
                              borderBottom: `2px solid ${crmColors.border.default}`,
                            }}
                          >
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                            <TransactionIcon
                              sx={{ fontSize: 40, color: crmColors.border.hover, mb: 1 }}
                            />
                            <Typography sx={{ fontWeight: 800, color: crmColors.text.secondary }}>
                              No payments
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredRows.map((r, idx) => {
                          const tone = statusTone(r.payment_status);
                          return (
                            <TableRow
                              key={`${r.transaction_id || idx}`}
                              hover
                              sx={{
                                "&:nth-of-type(even)": {
                                  bgcolor: alpha(crmColors.primary.main, 0.015),
                                },
                              }}
                            >
                              <TableCell>
                                <Typography
                                  sx={{
                                    fontWeight: 800,
                                    fontSize: 13,
                                    color: crmColors.primary.main,
                                  }}
                                >
                                  {r.transaction_id || "—"}
                                </Typography>
                                {r.gateway_transaction_id ? (
                                  <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 600, color: crmColors.text.secondary }}
                                  >
                                    PG: {r.gateway_transaction_id}
                                  </Typography>
                                ) : null}
                              </TableCell>
                              <TableCell>
                                <Typography sx={{ fontWeight: 800, fontSize: 13, lineHeight: 1.25 }}>
                                  {r.school_name || "—"}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ fontWeight: 600, color: crmColors.text.secondary }}
                                >
                                  {r.school_code || ""}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: 12.5 }}>
                                {r.invoice_id || r.order_id || "—"}
                                {r.product_name ? (
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    sx={{ fontWeight: 600, color: crmColors.text.secondary }}
                                  >
                                    {r.product_name}
                                  </Typography>
                                ) : null}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                                {r.payment_date
                                  ? new Date(r.payment_date).toLocaleDateString("en-IN")
                                  : "—"}
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: 12.5 }}>
                                {r.payment_channel || "Manual"}
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: 12.5 }}>
                                {r.payment_mode_name || r.payment_method || "—"}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={r.payment_status_name || r.payment_status || "—"}
                                  sx={{
                                    height: 22,
                                    fontWeight: 800,
                                    fontSize: 10.5,
                                    bgcolor: tone.bg,
                                    color: tone.fg,
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  fontWeight: 900,
                                  color: crmColors.success.dark,
                                  fontVariantNumeric: "tabular-nums",
                                }}
                              >
                                {formatMoney(r.transaction_amount)}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Box>
        )}
      </Stack>
    </PageContentLayout>
  );
};

export default TransactionPanel;
